// Layout.tsx
'use client';

import {useEffect, useState} from "react";
import BackToTop from '../elements/BackToTop';
import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";

interface LayoutProps {
	headerStyle?: number;
	footerStyle?: number;
	children?: React.ReactNode;
}

// Small helper that lazy-loads WOW + AOS after mount
function AnimationsBootstrap() {
	useEffect(() => {
		let wowInstance: any;

		(async () => {
			const [{ WOW }, AOS] = await Promise.all([
				import('wowjs').then(m => m.default || m),
				import('aos').then(m => m.default || m),
			]);

			wowInstance = new WOW({ live: false });
			wowInstance.init();

			AOS.init({
				once: true,      // animate only once → less work on scroll
				duration: 600,
			});
		})();

		return () => {
			// nothing specific to clean, WOW/AOS hook into scroll
			wowInstance = null;
		};
	}, []);

	return null;
}

export default function Layout({
								   headerStyle,
								   footerStyle,
								   children,
							   }: LayoutProps) {
	const [scroll, setScroll] = useState(false);
	const [isMobileMenu, setMobileMenu] = useState(false);
	const [isSearch, setSearch] = useState(false);
	const [isOffCanvas, setOffCanvas] = useState(false);

	const handleMobileMenu = () => {
		setMobileMenu(prev => {
			const next = !prev;
			if (next) {
				document.body.classList.add("mobile-menu-active");
			} else {
				document.body.classList.remove("mobile-menu-active");
			}
			return next;
		});
	};

	const handleSearch = () => setSearch(prev => !prev);
	const handleOffCanvas = () => setOffCanvas(prev => !prev);

	// More INP-friendly scroll handler
	useEffect(() => {
		let ticking = false;

		const handleScroll = () => {
			if (!ticking) {
				ticking = true;
				requestAnimationFrame(() => {
					const scrolled = window.scrollY > 100;
					setScroll(prev => (prev === scrolled ? prev : scrolled));
					ticking = false;
				});
			}
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		// set initial state
		handleScroll();

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	return (
		<>
			{/* lazy-load WOW/AOS once per page */}
			<AnimationsBootstrap />

			<div id="top" />

			<div className="d-flex flex-column min-vh-100">
				<Header
					scroll={scroll}
					isMobileMenu={isMobileMenu}
					handleMobileMenu={handleMobileMenu}
					isSearch={isSearch}
					handleSearch={handleSearch}
					isOffCanvas={isOffCanvas}
					handleOffCanvas={handleOffCanvas}
				/>

				<main className="flex-grow-1">
					{children}
				</main>

				<Footer />
			</div>

			<BackToTop target="#top" />
		</>
	);
}
